/* external */
import React, { useRef } from "react";
import _ from "lodash";
import classNames from "classnames";
import { Button } from "@toddle-edu/ds-web";
import { AddCircleOutlined, PencilLineOutlined } from "@toddle-edu/ds-icons";

/* internal */
import { FieldComponent } from "UnitPlans/components/IBcomponents";
import { getOptionsFromField } from "Worksheet/modules/WorksheetUtils";
import { useI18n } from "Hooks";
import { FIELD_UID_ADD_LEARNING_GOALS } from "Worksheet/modules/WorksheetConstants";
import { getSubFields } from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";

/* relative */
import classes from "./LearningGoalFields.scss";

const LearningGoalFields = props => {
  const {
    fieldList,
    assessmentFields,
    assessmentTools,
    assessmentType,
    curriculumType,
    courseId,
    templateId,
    unitPlanFieldList,
    unitPlanFields,
    handleOnUpdateField,
    learningGoalFields,
    learningGoalOptions,
    portalType,
    isFromWorksheetAIPanel,
    renderTitle,
    renderValue,
  } = props;
  const { t } = useI18n(["worksheet"]);
  const learningGoalFieldsRef = useRef({});
  const fieldUID = FIELD_UID_ADD_LEARNING_GOALS;

  // storing refs of all rendered fields
  const setAIModalFieldRef = (fieldUID, fieldRef) => {
    learningGoalFieldsRef.current[fieldUID] = fieldRef;
  };

  const handleLearningGoalOptionClick = option => {
    learningGoalFieldsRef?.current?.[
      FIELD_UID_ADD_LEARNING_GOALS
    ]?.onDropdownItemClick?.(option);
  };

  const defaultOptions = getOptionsFromField({
    fieldList,
    fieldUid: fieldUID,
    t,
  });

  const fieldObj = _.get(fieldList, fieldUID, {});
  const { config = {} } = fieldObj;

  const subFields = getSubFields({
    fieldKey: fieldUID,
    fieldObj: fieldObj,
    unitPlanFields,
  });

  const fieldContainerClasses = classNames({
    [classes.fieldContainer]: true,
    [classes.fieldContainerV2]: isFromWorksheetAIPanel,
  });

  return (
    <div className={fieldContainerClasses} key={fieldUID}>
      <FieldComponent
        assessmentFields={assessmentFields}
        assessmentTools={assessmentTools}
        assessmentType={assessmentType}
        curriculumType={curriculumType}
        customRef={setAIModalFieldRef}
        defaultOptions={defaultOptions}
        fieldKey={fieldUID}
        field_list={fieldList}
        selectedCourseId={courseId}
        sourceFrom={portalType}
        templateId={templateId}
        testId={`worksheetAIModal-AIModalFields-${fieldUID}`}
        unitPlanFieldList={unitPlanFieldList}
        unitPlanFields={unitPlanFields}
        updateInputField={handleOnUpdateField}
        subFields={subFields}
        {...config}
        type="AddLearningGoals"
        containerStyle={{ display: "none" }}
      />

      {_.map(learningGoalOptions, option => {
        const fieldLabel = _.get(option, "label", "");
        const fieldUID = _.get(option, "key", "");
        const fieldData = _.find(learningGoalFields, { uid: fieldUID });
        const selectedCount = _.size(_.get(fieldData, "value", []));

        const labelClasses = classNames(classes.textDefault, "text-label");
        const iconClasses = classNames(classes.iconSecondary, classes.icon16);

        const selectedClasses = classNames(
          classes.textSemanticInfo,
          "text-body"
        );

        const learningGoalContainerClasses = classNames({
          [classes.learningGoalContainer]: true,
          [classes.learningGoalContainerV2]: isFromWorksheetAIPanel,
        });
        return (
          <div className={learningGoalContainerClasses} key={fieldUID}>
            {renderTitle ? (
              renderTitle({ field: option })
            ) : (
              <div className={labelClasses} aria-label={fieldLabel}>
                {fieldLabel}
              </div>
            )}

            {renderValue ? (
              renderValue({
                field: {
                  ...option,
                  selectedCount,
                  onClick: handleLearningGoalOptionClick,
                },
              })
            ) : _.isEqual(selectedCount, 0) ? (
              <Button
                dsVersion="2.0"
                type="plain"
                variant="neutral"
                icon={
                  <AddCircleOutlined
                    size={"xxx-small"}
                    className={iconClasses}
                  />
                }
                testId={`worksheetAIModal-${fieldUID}`}
                onClick={() => handleLearningGoalOptionClick(option)}
                aria-label={t("common:add_with_label", {
                  label: fieldLabel,
                })}
                className={classes.selectButton}
              >
                {t("common:select")}
              </Button>
            ) : (
              <div className={classes.editContainer}>
                <span className={selectedClasses}>
                  {selectedCount} {t("common:selected")}
                </span>
                <Button
                  dsVersion="2.0"
                  className={classes.editButton}
                  icon={
                    <PencilLineOutlined className={classes.iconSecondary} />
                  }
                  onClick={() => handleLearningGoalOptionClick(option)}
                  variant="neutral"
                  type="plain"
                  size="x-small"
                  testId={`worksheetAIModal-${fieldUID}`}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LearningGoalFields;
